import { API_BASE_URL } from './env';
import { ApiError, parseErrorEnvelope } from './errors';

/**
 * Staff log in with the same magic link members use. There is no password to
 * reset and no separate staff credential store — holding a role assignment is
 * what makes someone staff, not how they authenticated.
 *
 * These two calls deliberately bypass `req()`: it redirects to the login page
 * on a 401, which would loop if the login page itself used it.
 */

/** Where to send someone back to after they log in. */
const RETURN_TO_KEY = 'mib_admin_return_to';

export function rememberReturnTo(path: string) {
  // Only same-origin paths. An absolute URL here would turn the login flow
  // into an open redirect.
  if (path.startsWith('/') && !path.startsWith('//')) {
    sessionStorage.setItem(RETURN_TO_KEY, path);
  }
}

export function takeReturnTo(): string {
  const stored = sessionStorage.getItem(RETURN_TO_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);
  return stored && stored.startsWith('/') && !stored.startsWith('//') ? stored : '/';
}

async function post(path: string, body: unknown): Promise<Response> {
  try {
    return await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
  } catch (networkErr) {
    throw new ApiError(0, 'NETWORK', `network error: ${String(networkErr)}`);
  }
}

/**
 * Asks for a login link. `client: 'admin'` makes the emailed link point at the
 * dashboard rather than the public site.
 *
 * Resolves the same way whether or not the address belongs to anyone, because
 * the API answers identically — the endpoint must not reveal who has an
 * account, and the UI must not undo that by rendering a different message.
 */
export async function requestLoginLink(email: string): Promise<void> {
  const res = await post('/v1/auth/request', { email, client: 'admin' });
  if (res.status === 202) return;

  const body = await res.json().catch(() => ({}));
  throw parseErrorEnvelope(res.status, body);
}

/** Exchanges the emailed token for a session cookie. */
export async function completeLogin(token: string): Promise<void> {
  const res = await post('/v1/auth/callback', { token });
  if (res.ok) return;

  const body = await res.json().catch(() => ({}));
  throw parseErrorEnvelope(res.status, body);
}

export async function logout(): Promise<void> {
  await post('/v1/auth/logout', {});
}

/**
 * Password sign-in. An alternative to the link, not a replacement: passwords
 * are optional and most staff will not have one.
 *
 * The API answers identically for an unknown address, an account with no
 * password, a wrong password and a suspended account, so there is nothing more
 * specific to show than "that did not work".
 */
export async function signInWithPassword(email: string, password: string): Promise<void> {
  const res = await post('/v1/auth/password', { email, password });
  if (res.ok) return;

  const body = await res.json().catch(() => ({}));
  throw parseErrorEnvelope(res.status, body);
}

/** Sets or clears the signed-in person's own password. Null clears it. */
export async function setOwnPassword(password: string | null): Promise<void> {
  const res = await post('/v1/auth/set-password', { password });
  if (res.ok) return;

  const body = await res.json().catch(() => ({}));
  throw parseErrorEnvelope(res.status, body);
}
